/*
 * Copyright (c) 2024, King Living
 * Custom Hooks for Google Maps Integration
 * GM-008: Geolocation on page load
 * GM-010: "Use my location" functionality
 *
 * Uses Google Geocoding API for address-to-coordinates conversion
 * Uses SFCC Shopper Stores API for store search
 */

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useSearchStores} from '@salesforce/commerce-sdk-react';

import {
  DEFAULT_STORE_LIMIT,
  DEFAULT_STORE_SEARCH_RADIUS,
  FALLBACK_STORE_LIMIT,
  MAX_STORE_SEARCH_RADIUS,
} from '../constants/site-config';
import {useGeocoding} from './use-geocoding';

/** Degrees — geocoded point vs site default; avoids duplicate fallback API calls. */
const COORD_EPSILON = 1e-4;

/**
 * KL-988 — map framing on search. The store API is queried with a very large radius (so the list
 * always has results), which previously made the map fit ALL returned markers and zoom out to a
 * whole country/quarter-continent. Instead we frame the map on the NEAREST cluster of showrooms:
 * the closest store plus any others within a distance window of it (so a searched city/suburb/state
 * shows its local showrooms, not empty outback or the whole continent).
 */
const FOCUS_MAX_STORES = 6;

/** Always include stores within at least this many km of the nearest one (keeps a city-metro view). */
const FOCUS_MIN_SPAN_KM = 35;

/** ...and any others up to this multiple of the nearest store's distance (drops far-flung outliers). */
const FOCUS_DISTANCE_MULTIPLIER = 2;
const MILES_TO_KM = 1.60934;

const coordsNearEqual = (lat1, lng1, lat2, lng2) => Math.abs(lat1 - lat2) < COORD_EPSILON &&
  Math.abs(lng1 - lng2) < COORD_EPSILON;

const isValidLatLng = (lat, lng) => lat != null && lng != null && !Number.isNaN(lat) &&
  !Number.isNaN(lng);

const haversineDistanceKm = (lat1, lng1, lat2, lng2) => {
  const toRad = (deg) => deg * (Math.PI / 180);
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const sinHalfDLat = Math.sin(dLat / 2);
  const sinHalfDLng = Math.sin(dLng / 2);
  const a =
    (sinHalfDLat * sinHalfDLat) +
    (Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * sinHalfDLng * sinHalfDLng);

  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const storeLatLng = (store) => ({
  lat: Number(store?.latitude ?? store?.coordinates?.lat),
  lng: Number(store?.longitude ?? store?.coordinates?.lng),
});

// Nearest showroom cluster bounds for map focusBounds.
export function computeStoreClusterFocusBounds(stores, {distanceUnit = 'km', origin} = {}) {
  if (!stores?.length) return null;

  const unitToKm = distanceUnit === 'miles' ? MILES_TO_KM : 1;
  const ranked = stores
    .map((store) => {
      let distanceKm = Infinity;

      if (store.distance != null && Number.isFinite(store.distance)) {
        distanceKm = Math.max(0, store.distance * unitToKm);
      } else if (origin?.lat != null && origin?.lng != null) {
        const {lat, lng} = storeLatLng(store);

        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          distanceKm = haversineDistanceKm(origin.lat, origin.lng, lat, lng);
        }
      }

      return {store, distanceKm};
    })
    .sort((a, b) => a.distanceKm - b.distanceKm);

  const nearestKm = Math.max(0, ranked[0].distanceKm);
  const windowKm = Math.max(nearestKm * FOCUS_DISTANCE_MULTIPLIER, FOCUS_MIN_SPAN_KM);
  const selected = [];

  for (const {store, distanceKm} of ranked) {
    if (selected.length >= FOCUS_MAX_STORES) break;

    if (distanceKm <= windowKm && isValidLatLng(store.latitude, store.longitude)) {
      selected.push(store);
    }
  }

  if (selected.length === 0) {
    const first = ranked[0].store;

    if (!isValidLatLng(first.latitude, first.longitude)) return null;
    selected.push(first);
  }

  return selected.reduce(
    (bounds, store) => ({
      north: Math.max(bounds.north, store.latitude),
      south: Math.min(bounds.south, store.latitude),
      east: Math.max(bounds.east, store.longitude),
      west: Math.min(bounds.west, store.longitude),
    }),
    {north: -90, south: 90, east: -180, west: 180}
  );
}

/**
 * Geolocation Hook
 * Handles browser geolocation API for getting user's current position
 */
export function useGeolocation() {
  const [position, setPosition] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getCurrentPosition = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        setError('Geolocation is not supported by this browser');

        return reject(new Error('Geolocation not supported'));
      }

      setIsLoading(true);
      setError(null);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          };

          setPosition(coords);
          setIsLoading(false);
          resolve(coords);
        },
        (geoError) => {
          let errorMessage = 'Unable to retrieve your location';

          switch (geoError.code) {
            case geoError.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user';
              break;
            case geoError.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable';
              break;
            case geoError.TIMEOUT:
              errorMessage = 'Location request timed out';
              break;
            default:
              errorMessage = 'An unknown error occurred';
              break;
          }

          setError(errorMessage);
          setIsLoading(false);
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  }, []);

  return {
    position,
    isLoading,
    error,
    getCurrentPosition,
  };
}

/**
 * Custom marker icon SVG with "K" character for King Living
 * Black square shape with arrow tail and white "K" letter
 */
const KING_MARKER_ICON_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" width="32" height="38" viewBox="0 0 32 38">
  <rect x="0" y="0" width="32" height="32" fill="#000000"/>
  <polygon points="12,32 20,32 16,38" fill="#000000"/>
  <text x="16" y="21" font-family="Arial, sans-serif" font-size="16" font-weight="bold" fill="#FFFFFF" text-anchor="middle">K</text>
</svg>
`;

/**
 * Default marker icon sizes by breakpoint (exported for consumers)
 * - Desktop (lg+): 50x50
 * - Mobile (base): 32x38 (matches SVG viewBox aspect)
 */
export const MARKER_ICON_SIZES = {
  desktop: {width: 50, height: 50},
  mobile: {width: 32, height: 38},
};

export const MARKER_ICON_SIZES_SHOWROOM_DETAILS = {
  desktop: {width: 50, height: 50},
  mobile: {width: 50, height: 50},
};

/**
 * Responsive marker dimensions for the custom King map pin (Chakra `base` / `lg`).
 *
 * @param {Object} [options]
 * @param {boolean} [options.isShowroomDetail=false] - When true, use {@link MARKER_ICON_SIZES_SHOWROOM_DETAILS} (single-showroom map)
 * @returns {{width: number, height: number}} Size for the current breakpoint
 */
export function useMarkerIconSize({isShowroomDetail = false} = {}) {
  const sizes = isShowroomDetail ? MARKER_ICON_SIZES_SHOWROOM_DETAILS : MARKER_ICON_SIZES;

  return useBreakpointValue({
    base: sizes.mobile,
    lg: sizes.desktop,
  }) ?? sizes.desktop;
}

/**
 * Get the custom marker icon as a data URL with optional responsive size
 * @param {Object} [size] - Display size in pixels
 * @param {number} [size.width=50] - Icon width
 * @param {number} [size.height=50] - Icon height
 * @returns {Object} Custom marker icon object for Google Maps
 */
export const getCustomMarkerIcon = (size = MARKER_ICON_SIZES.desktop) => {
  const width = size?.width ?? MARKER_ICON_SIZES.desktop.width;
  const height = size?.height ?? MARKER_ICON_SIZES.desktop.height;
  const encodedSvg = encodeURIComponent(KING_MARKER_ICON_SVG.trim());

  return {
    url: `data:image/svg+xml,${encodedSvg}`,
    scaledSize: {width, height},
    anchor: {x: width / 2, y: height}, // Tip of the arrow tail (bottom center)
  };
};

/**
 * Create store markers from SFCC store data
 * @param {Array} stores - Array of stores from SFCC API
 * @param {Object} [iconSize] - Marker icon size { width, height } for responsive display
 * @returns {Array} Array of marker objects for GoogleMap component
 */
export const createStoreMarkers = (stores, iconSize = MARKER_ICON_SIZES.desktop) => {
  if (!stores || stores.length === 0) return [];

  const customIcon = getCustomMarkerIcon(iconSize);

  return stores.reduce((markers, store) => {
    const lat = Number(store?.latitude ?? store?.coordinates?.lat);
    const lng = Number(store?.longitude ?? store?.coordinates?.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return markers;
    }

    markers.push({
      position: {
        lat,
        lng,
      },
      storeId: store.id,
      title: store.name,
      icon: customIcon,
    });

    return markers;
  }, []);
};

/**
 * Main Google Maps Integration Hook
 * Combines geocoding, geolocation and store search functionality
 *
 * @param {Object} options - Configuration options
 * @param {string} options.countryCode - Country code for geocoding bias (e.g., 'AU', 'US')
 * @param {string} options.locale - Locale for API requests (e.g., 'en-AU')
 * @param {string} options.distanceUnit - Distance unit for store search ('km' or 'miles')
 * @param {Object} [options.defaultLocation] - Site default { lat, lng }: denied GPS / fallbacks when a search returns no stores
 * @param {number} [options.searchRadius] - Radius (km) for address/postcode and "Use my location" searches
 * @param {React.MutableRefObject<boolean>} [options.initialSearchUsedUserCoordsRef] - True after initial load used GPS; if 0 stores, one fallback to defaultLocation (limit 20)
 * @param {boolean} [options.isShowroomDetail=false] - Use showroom-details marker sizes (larger on mobile)
 * @returns {Object} Integration state and methods
 */
export function useGoogleMapsIntegration({
  countryCode = 'AU',
  locale = 'en-AU',
  distanceUnit = 'km',
  searchRadius = DEFAULT_STORE_SEARCH_RADIUS,
  defaultLocation = null,
  initialSearchUsedUserCoordsRef = null,
  isShowroomDetail = false,
} = {}) {
  const markerIconSize = useMarkerIconSize({isShowroomDetail});

  /**
   * Next store search came from address or "Use my location" — enables empty → default fallback.
   */
  const userInitiatedStoreSearchRef = useRef(false);

  /**
   * User-initiated empty fallback already handled or skipped (e.g. already at default coords).
   */
  const userEmptyFallbackAppliedRef = useRef(false);

  const armUserInitiatedStoreSearch = useCallback(() => {
    userInitiatedStoreSearchRef.current = true;
    userEmptyFallbackAppliedRef.current = false;
  }, []);

  const disarmUserInitiatedStoreSearch = useCallback(() => {
    userEmptyFallbackAppliedRef.current = true;
    userInitiatedStoreSearchRef.current = false;
  }, []);

  // Map state
  const [mapCenter, setMapCenter] = useState(null);
  const [markers, setMarkers] = useState([]);

  const [isFallbackResults, setIsFallbackResults] = useState(false);

  // Search state: initial load uses max radius; address search uses searchRadius
  const [searchParams, setSearchParams] = useState({
    latitude: null,
    longitude: null,
    maxDistance: MAX_STORE_SEARCH_RADIUS,
    distanceUnit,
    limit: DEFAULT_STORE_LIMIT,
    locale,
  });
  const [searchError, setSearchError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Initialize hooks
  const geolocation = useGeolocation();
  const geocoding = useGeocoding();

  // SFCC Store Search API
  const {
    data: storesData,
    isLoading: isStoresLoading,
    error: storesApiError,
    refetch: refetchStores,
  } = useSearchStores(
    {
      parameters: {
        latitude: searchParams.latitude,
        longitude: searchParams.longitude,
        maxDistance: searchParams.maxDistance,
        distanceUnit: searchParams.distanceUnit,
        limit: searchParams.limit,
        locale: searchParams.locale,
      },
    },
    {
      // Only fetch when we have coordinates
      enabled: searchParams.latitude !== null && searchParams.longitude !== null,
    }
  );

  // Extract stores from API response and sort by distance (nearest first)
  const stores = useMemo(() => {
    const storeList = storesData?.data || [];

    return [...storeList].sort(
      (a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity)
    );
  }, [storesData?.data]);

  /**
   * Search stores by coordinates (updates map center and SFCC query params).
   *
   * @param {Object} params - Search parameters
   * @param {number} params.latitude - Latitude
   * @param {number} params.longitude - Longitude
   * @param {number} [params.maxDistance] - km; defaults to {@link MAX_STORE_SEARCH_RADIUS}
   * @param {number} [params.limit] - defaults to {@link DEFAULT_STORE_LIMIT}
   */
  const searchByCoordinates = useCallback(async ({
    latitude,
    longitude,
    maxDistance,
    limit,
  }) => {
    setSearchError(null);

    const effectiveRadius = maxDistance ?? MAX_STORE_SEARCH_RADIUS;
    const effectiveLimit = limit ?? DEFAULT_STORE_LIMIT;

    // Update search params to trigger store search
    setSearchParams((prev) => ({
      ...prev,
      latitude,
      longitude,
      maxDistance: effectiveRadius,
      limit: effectiveLimit,
    }));

    // Update map center
    setMapCenter({
      lat: latitude,
      lng: longitude,
    });

    return {latitude, longitude};
  }, []);

  // User address / "Use my location": 0 stores → one search at site default (max radius, limit 10).
  useEffect(() => {
    if (
      isStoresLoading ||
      storesApiError ||
      searchParams.latitude === null ||
      searchParams.longitude === null ||
      stores.length > 0 ||
      !userInitiatedStoreSearchRef.current ||
      userEmptyFallbackAppliedRef.current ||
      !isValidLatLng(defaultLocation?.lat, defaultLocation?.lng)
    ) {
      return;
    }

    const {latitude: lat, longitude: lng} = searchParams;
    const {lat: defLat, lng: defLng} = defaultLocation;

    if (coordsNearEqual(lat, lng, defLat, defLng)) {
      disarmUserInitiatedStoreSearch();

      return;
    }

    disarmUserInitiatedStoreSearch();
    setIsFallbackResults(true);
    searchByCoordinates({
      latitude: defLat,
      longitude: defLng,
      maxDistance: MAX_STORE_SEARCH_RADIUS,
      limit: FALLBACK_STORE_LIMIT,
    });
  }, [
    disarmUserInitiatedStoreSearch,
    isStoresLoading,
    storesApiError,
    stores.length,
    searchParams.latitude,
    searchParams.longitude,
    defaultLocation,
    searchByCoordinates
  ]);

  // Initial page load used GPS and returned 0 stores → one search at default (max radius, limit 20).
  useEffect(() => {
    if (
      stores.length > 0 ||
      isStoresLoading ||
      storesApiError ||
      searchParams.latitude === null ||
      !isValidLatLng(defaultLocation?.lat, defaultLocation?.lng)
    ) {
      return;
    }

    if (!initialSearchUsedUserCoordsRef?.current) return;

    initialSearchUsedUserCoordsRef.current = false;
    setIsFallbackResults(true);
    searchByCoordinates({
      latitude: defaultLocation.lat,
      longitude: defaultLocation.lng,
      limit: DEFAULT_STORE_LIMIT,
    });
  }, [
    stores.length,
    isStoresLoading,
    storesApiError,
    searchParams.latitude,
    defaultLocation,
    initialSearchUsedUserCoordsRef,
    searchByCoordinates
  ]);

  // Update markers when stores change (uses current responsive icon size)
  const updateMarkersFromStores = useCallback((storeList) => {
    const storeMarkers = createStoreMarkers(storeList, markerIconSize);

    setMarkers(storeMarkers);
  }, [markerIconSize]);

  /**
   * GM-010: Handle "Use my location" button click
   * Gets user's current position via browser geolocation API
   * Then searches for stores near that location.
   * When geolocation is inaccessible, falls back to site default (20 stores, max radius).
   *
   * @returns {Promise<{coords?: Object, usedFallback: boolean, error?: Error}>}
   */
  const handleUseMyLocation = useCallback(async () => {
    setSearchError(null);
    setIsFallbackResults(false);
    setSearchQuery('');

    try {
      const coords = await geolocation.getCurrentPosition();

      armUserInitiatedStoreSearch();

      await searchByCoordinates({
        latitude: coords.latitude,
        longitude: coords.longitude,
        maxDistance: searchRadius,
      });

      return {coords, usedFallback: false};
    } catch (geoError) {
      if (!isValidLatLng(defaultLocation?.lat, defaultLocation?.lng)) {
        throw geoError;
      }

      // KINDW-1383: inaccessible location → default 20 stores at site default (TSD)
      setIsFallbackResults(true);
      await searchByCoordinates({
        latitude: defaultLocation.lat,
        longitude: defaultLocation.lng,
        maxDistance: MAX_STORE_SEARCH_RADIUS,
        limit: DEFAULT_STORE_LIMIT,
      });

      return {usedFallback: true, error: geoError};
    }
  }, [
    armUserInitiatedStoreSearch,
    defaultLocation,
    geolocation,
    searchByCoordinates,
    searchRadius
  ]);

  /**
   * Handle location search by address query
   * Uses Google Geocoding API to convert address to coordinates
   * Then searches for stores near that location
   *
   * @param {string} query - Address/suburb/postcode to search
   */
  const handleLocationSearch = useCallback(async (query) => {
    setSearchError(null);
    setIsFallbackResults(false);
    setSearchQuery(query);

    // Manual search replaces initial-page GPS context so only the initial-load fallback runs for that ref.
    if (initialSearchUsedUserCoordsRef) {
      initialSearchUsedUserCoordsRef.current = false;
    }

    try {
      // Use Google Geocoding API with country filter
      const geocodeResult = await geocoding.geocodeWithCountry(query, countryCode);

      // Extract coordinates from first result
      const coordinates = geocoding.extractCoordinates(geocodeResult);

      if (!coordinates) {
        // Format error message for invalid search term
        const error = `NO_RESULTS:${query}`;

        setSearchError(error);
        throw new Error(error);
      }

      armUserInitiatedStoreSearch();

      await searchByCoordinates({
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        maxDistance: searchRadius,
      });

      return coordinates;
    } catch (error) {
      // Check if it's a configuration error (API key missing)
      const isConfigError = error.message?.includes('503') ||
        error.message?.includes('CONFIGURATION_ERROR') ||
        error.message?.includes('not configured');

      let errorMessage;

      if (isConfigError) {
        errorMessage = 'Geocoding service is not available. Please try again later.';
        console.warn('[Google Maps Integration] Geocoding service not configured:', error.message);
      } else {
        errorMessage = `No results found for "${query}". Please try a different search term.`;
      }

      setSearchError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [armUserInitiatedStoreSearch, countryCode, geocoding, initialSearchUsedUserCoordsRef, searchByCoordinates, searchRadius]);

  /**
   * KL-991: Search stores from a Google Places Autocomplete selection.
   *
   * Resolves the chosen prediction's `place_id` to coordinates via `/server/geocode`
   * (Place Details) — more precise than re-geocoding the free-text description — then
   * runs the same store search as {@link handleLocationSearch}, reusing its
   * user-initiated fallback (0 results → site default) and error/searchQuery semantics.
   *
   * @param {string} placeId - Google Places `place_id` from the selected prediction
   * @param {string} [label] - Human-readable prediction text (for searchQuery + error copy)
   * @returns {Promise<{lat: number, lng: number}>} Resolved coordinates
   */
  const handlePlaceSelect = useCallback(async (placeId, label = '') => {
    setSearchError(null);
    setIsFallbackResults(false);
    setSearchQuery(label);

    // Manual search replaces initial-page GPS context so only the initial-load fallback runs for that ref.
    if (initialSearchUsedUserCoordsRef) {
      initialSearchUsedUserCoordsRef.current = false;
    }

    try {
      const geocodeResult = await geocoding.lookupPlaceById(placeId);
      const coordinates = geocoding.extractCoordinates(geocodeResult);

      if (!coordinates) {
        const error = `NO_RESULTS:${label}`;

        setSearchError(error);
        throw new Error(error);
      }

      armUserInitiatedStoreSearch();

      await searchByCoordinates({
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        maxDistance: searchRadius,
      });

      return coordinates;
    } catch (error) {
      const isConfigError = error.message?.includes('503') ||
        error.message?.includes('CONFIGURATION_ERROR') ||
        error.message?.includes('not configured');

      let errorMessage;

      if (isConfigError) {
        errorMessage = 'Geocoding service is not available. Please try again later.';
        console.warn('[Google Maps Integration] Geocoding service not configured:', error.message);
      } else if (error.message?.startsWith('NO_RESULTS:')) {
        errorMessage = error.message;
      } else {
        errorMessage = `No results found for "${label}". Please try a different search term.`;
      }

      setSearchError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [armUserInitiatedStoreSearch, geocoding, initialSearchUsedUserCoordsRef, searchByCoordinates, searchRadius]);

  /**
   * GM-008: Load stores on page load using default location
   * Uses max radius so stores are always returned when available (no fallback needed).
   */
  const loadStoresOnPageLoad = useCallback(async (defaultLocation) => {
    if (!defaultLocation?.lat || !defaultLocation?.lng) {
      console.warn('No default location provided for initial store load');

      return [];
    }

    await searchByCoordinates({
      latitude: defaultLocation.lat,
      longitude: defaultLocation.lng,
    });

    return stores;
  }, [searchByCoordinates, stores]);

  // Manual marker update - called after stores are loaded
  const updateMarkers = useCallback(() => {
    if (stores.length > 0) {
      updateMarkersFromStores(stores);
    }
  }, [stores, updateMarkersFromStores]);

  // Memoize final markers (recompute when stores or responsive icon size changes)
  const finalMarkers = useMemo(() => {
    if (stores.length > 0) {
      return createStoreMarkers(stores, markerIconSize);
    }
    if (markers.length > 0) {
      return markers;
    }

    return [];
  }, [markers, stores, markerIconSize]);

  const mapBounds = useMemo(
    () => computeStoreClusterFocusBounds(stores, {distanceUnit}),
    [stores, distanceUnit]
  );

  return {
    // Geolocation state
    geolocationError: geolocation.error,
    isGeolocationLoading: geolocation.isLoading,
    getCurrentPosition: geolocation.getCurrentPosition,

    // Geocoding state
    geocodingError: geocoding.error,
    isGeocodingLoading: geocoding.loading,

    // Store search state
    stores,
    searchError: searchError || (storesApiError?.message),
    isSearchLoading: isStoresLoading || geocoding.loading,
    isFallbackResults,
    searchQuery,

    // Map state
    mapCenter,
    mapBounds,
    markers: finalMarkers,

    // Actions
    loadStoresOnPageLoad,
    handleUseMyLocation,
    handleLocationSearch,
    handlePlaceSelect,
    searchByCoordinates,
    refetchStores,
    updateMarkers,
  };
}
